/**
 * WeaveIn.ai Platform - Service Referrals API Service
 * TypeScript service layer for service referral management
 * Integrates with migrations 113-120: taxonomy, delivery tracking, milestones, billing
 */

import { AxiosInstance } from 'axios';
import { apiClient } from '../apiClient';
import { API_VERSION } from './organizations';
import { initializeActiveCboId } from '../../utils/auth';

// ============================================================================
// TYPE DEFINITIONS - SERVICE TAXONOMY
// ============================================================================

export interface ServiceDomain {
  id: number;
  code: string;
  name: string;
  description?: string;
  icon_name?: string;
  display_order: number;
  is_active: boolean;
}

export interface ServiceCategory {
  id: number;
  domain_id: number;
  domain_code?: string;
  domain_name?: string;
  code: string;
  name: string;
  description?: string;
  display_order: number;
  is_active: boolean;
}

export interface ServiceType {
  id: number;
  category_id: number;
  category_code?: string;
  category_name?: string;
  domain_code?: string;
  domain_name?: string;
  code: string;
  name: string;
  description?: string;
  default_units?: number;
  unit_type?: string;
  default_duration_days?: number;
  requires_prior_auth: boolean;
  billing_codes?: Record<string, any>;
  is_active: boolean;
}

export interface ServiceTypeSearchResult {
  id: number;
  code: string;
  name: string;
  description?: string;
  category_name: string;
  domain_name: string;
}

// ============================================================================
// TYPE DEFINITIONS - SDOH ASSESSMENT
// ============================================================================

export type SDOHDomainCode =
  | 'HOUSING'
  | 'FOOD_NUTRITION'
  | 'TRANSPORTATION'
  | 'ECONOMIC_STABILITY'
  | 'EDUCATION_EMPLOYMENT'
  | 'SOCIAL_SUPPORT'
  | 'HEALTH_BEHAVIORS'
  | 'SAFETY';

export type SDOHSeverity = 'low' | 'moderate' | 'high' | 'critical';

export interface SDOHAssessmentData {
  id: number;
  assessment_type: 'screening' | 'full_assessment' | 're_assessment' | 'follow_up';
  assessment_tool?: string;
  assessed_at: string;
  overall_sdoh_score?: number;
  risk_level?: SDOHSeverity;
  priority_domains?: string[];
  housing_score?: number;
  food_score?: number;
  transportation_score?: number;
  economic_score?: number;
  education_employment_score?: number;
  social_support_score?: number;
  health_behaviors_score?: number;
  safety_score?: number;
  identified_needs?: Array<{
    domain: string;
    description: string;
    severity: string;
  }>;
  recommended_services?: Array<{
    service_type_code: string;
    service_type_name: string;
  }>;
  requires_follow_up?: boolean;
  follow_up_date?: string;
  notes?: string;
}

export interface SDOHNeedData {
  id: number;
  sdoh_domain_code: SDOHDomainCode;
  sdoh_category_code?: string;
  need_description?: string;
  severity: SDOHSeverity;
  is_primary: boolean;
  status: 'identified' | 'being_addressed' | 'resolved' | 'deferred';
  created_at?: string;
}

// ============================================================================
// TYPE DEFINITIONS - SERVICE REFERRALS
// ============================================================================

export type ReferralStatus =
  | 'created'
  | 'pending_consent'
  | 'consent_obtained'
  | 'matching'
  | 'matched'
  | 'provider_notified'
  | 'provider_accepted'
  | 'provider_declined'
  | 'service_scheduled'
  | 'service_started'
  | 'service_completed'
  | 'billing_ready'
  | 'billed'
  | 'paid'
  | 'closed'
  | 'cancelled'
  | 'failed';

export type UrgencyLevel = 'routine' | 'urgent' | 'emergency';

export interface ServiceReferral {
  id: number;
  referral_number: string;
  employee_id: number;
  employer_id: number;
  service_type_code: string;
  service_type_name?: string;
  status: ReferralStatus;
  urgency: UrgencyLevel;
  priority_score?: number;
  assigned_provider_id?: number;
  assigned_provider_name?: string;
  referral_reason?: string;
  clinical_notes?: string;
  consent_obtained: boolean;
  consent_date?: string;
  consent_document_id?: number;
  requested_start_date?: string;
  actual_start_date?: string;
  estimated_end_date?: string;
  actual_end_date?: string;
  estimated_units?: number;
  actual_units?: number;
  appointment_type?: string;
  appointment_location?: string;
  care_plan_summary?: string;
  outcome_status?: string;
  outcome_notes?: string;
  satisfaction_score?: number;
  created_at: string;
  updated_at: string;
  created_by_id?: number;
  // Provider selection fields
  selected_provider_id?: number;
  selected_provider_type?: string;
  selected_provider_name?: string;
  // Pricing proposal fields
  active_proposal_id?: number;
  accepted_proposal_id?: number;
  pricing_model?: string;
  contracted_amount?: number;
  // SDOH assessment fields
  sdoh_domain_code?: string;
  sdoh_assessment_id?: number;
  sdoh_risk_score?: number;
  sdoh_assessment?: SDOHAssessmentData;
  sdoh_needs?: SDOHNeedData[];
  // Clinical<->social handoff: clinician loop-back (outcome pushed back to the PCP)
  pcp_notification_sent?: boolean;
  pcp_notified_at?: string;
  origin_clinical_trigger?: 'diagnosis' | 'care_gap' | 'discharge' | 'screen';
  origin_icd10?: string;
}

export interface ReferralMatch {
  id: number;
  provider_type: string;
  provider_id: string | number;
  provider_name: string;
  match_score?: number;
  distance_miles?: number;
  match_rank?: number;
  is_selected: boolean;
  response_status: 'pending' | 'accepted' | 'declined' | 'expired';
  notified_at?: string;
  response_at?: string;
  provider_phone?: string;
  provider_email?: string;
  provider_address?: string;
  phi_released?: boolean;
  communication_count?: number;
  last_communication_at?: string;
}

export interface ServiceReferralCreate {
  employee_id: number;
  service_type_code: string;
  urgency?: UrgencyLevel;
  referral_reason?: string;
  clinical_notes?: string;
  requested_start_date?: string;
  estimated_units?: number;
  preferred_provider_id?: number;
}

export interface ServiceReferralUpdate {
  status?: ReferralStatus;
  urgency?: UrgencyLevel;
  referral_reason?: string;
  clinical_notes?: string;
  assigned_provider_id?: number;
  requested_start_date?: string;
  estimated_end_date?: string;
  estimated_units?: number;
  appointment_type?: string;
  appointment_location?: string;
  care_plan_summary?: string;
}

export interface ServiceReferralListResponse {
  referrals: ServiceReferral[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// TYPE DEFINITIONS - CONSENT-REFERRAL INTEGRATION
// ============================================================================

export interface ConsentContext {
  referral_id: number;
  referral_number: string;
  consent_id: number;
  consent_status: string;
  consent_version?: string;
  consent_text?: string;
  consent_obtained_at?: string;
  signature_method?: string;
  program_id: number;
  program_name: string;
  program_description?: string;
  employer_id: number;
  employer_name: string;
  employee_id: number;
  employee_name: string;
  employee_email?: string;
  link_type: string;
  link_active: boolean;
  link_created_at?: string;
}

export interface ConsentReferralLink {
  id: number;
  referral_number: string;
  status: ReferralStatus;
  service_type_code: string;
  service_type_name?: string;
  urgency_level: UrgencyLevel;
  created_at: string;
  link_type: string;
  is_active: boolean;
}

export interface ConsentSourceReferral {
  id: number;
  referral_number: string;
  status: ReferralStatus;
  service_type_code: string;
  service_type_name?: string;
  urgency_level: UrgencyLevel;
  created_at: string;
  consent_id?: number;
  consent_accepted_at?: string;
  employee_name?: string;
  employer_name?: string;
}

export interface PendingConsentForReferral {
  consent_id: number;
  employee_id: number;
  employee_name: string;
  employee_email?: string;
  program_id: number;
  program_name: string;
  employer_id: number;
  employer_name: string;
  accepted_at: string;
  consent_version?: string;
  service_type_code?: string;
  service_type_name?: string;
  urgency_level?: string;
}

// ============================================================================
// TYPE DEFINITIONS - STAFF/ADMIN ACTIONS
// ============================================================================

export interface StaffConsentAcceptResponse {
  success: boolean;
  message: string;
  consent_id: number;
  program_id: number;
  program_name: string;
  member_id: number;
  enrollment_id: number;
  enrollment_status: string;
  accepted_by: string;
  referral?: {
    id: number;
    referral_number: string;
    status: string;
  };
}

export interface StaffManualReferralResponse {
  success: boolean;
  message: string;
  referral_id: number;
  referral_number: string;
  service_type_code: string;
  service_type_name: string;
  status: string;
  matched_providers_count: number;
  created_by: string;
}

export interface StatusTransitionResponse {
  success: boolean;
  message: string;
  referral_id: number;
  previous_status: string;
  new_status: string;
}

export interface EmployeeConsentRecord {
  consent_id: number;
  program_id: number;
  program_name: string;
  status: string;
  offered_at: string | null;
  accepted_at: string | null;
  signature_method: string | null;
}

export interface EmployeeReferralRecord {
  referral_id: number;
  referral_number: string;
  service_type_code: string;
  service_type_name: string | null;
  status: string;
  urgency: string;
  source_type: string;
  created_at: string | null;
}

export interface EmployeeWorkflowRecord {
  workflow_id: number;
  template_name: string;
  status: string;
  outcome: string | null;
  initiated_at: string | null;
  completed_at: string | null;
  referral_created: boolean;
}

export interface AvailableProgram {
  program_id: number;
  program_name: string;
  description: string | null;
}

export interface WorkflowTemplate {
  template_id: number;
  template_name: string;
  template_type: string;
  description: string | null;
}

export interface WorkflowInitiateResponse {
  success: boolean;
  message: string;
  workflow_id: number;
  template_id: number;
  template_name: string;
  status: string;
  total_steps: number;
  initiated_by: string;
}

export interface EmployeeConsentReferralStatus {
  employee: {
    id: number;
    name: string;
    email: string | null;
    department: string | null;
    job_title: string | null;
    member_id: number | null;
    employer_id: number;
    employer_name: string;
  };
  consents: EmployeeConsentRecord[];
  referrals: EmployeeReferralRecord[];
  workflows: EmployeeWorkflowRecord[];
  available_programs: AvailableProgram[];
  workflow_templates: WorkflowTemplate[];
  summary: {
    has_accepted_consent: boolean;
    has_pending_consent: boolean;
    has_active_referral: boolean;
    has_active_workflow: boolean;
    total_consents: number;
    total_referrals: number;
    total_workflows: number;
  };
}

// ============================================================================
// TYPE DEFINITIONS - SERVICE DELIVERY PROGRESS
// ============================================================================

export type SessionType = 'regular' | 'assessment' | 'follow_up' | 'crisis' | 'discharge' | 'intake';

export interface ServiceProgress {
  id: number;
  referral_id: number;
  provider_id: number;
  session_date: string;
  session_type: SessionType;
  duration_minutes?: number;
  units_delivered: number;
  progress_notes: string;
  goals_addressed?: string[];
  interventions_used?: string[];
  client_response?: string;
  next_steps?: string;
  staff_name?: string;
  staff_credential?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: number;
}

export interface ServiceProgressCreate {
  session_date: string;
  session_type?: SessionType;
  duration_minutes?: number;
  units_delivered?: number;
  progress_notes: string;
  goals_addressed?: string[];
  interventions_used?: string[];
  client_response?: string;
  next_steps?: string;
  staff_name?: string;
  staff_credential?: string;
}

// ============================================================================
// TYPE DEFINITIONS - SERVICE GOALS
// ============================================================================

export type GoalStatus = 'active' | 'achieved' | 'partially_achieved' | 'discontinued' | 'modified';
export type GoalOutcome = 'met' | 'partially_met' | 'not_met' | 'ongoing';

export interface ServiceGoal {
  id: number;
  referral_id: number;
  goal_number: number;
  goal_description: string;
  goal_category?: string;
  target_date?: string;
  status: GoalStatus;
  progress_percentage: number;
  last_progress_date?: string;
  progress_notes?: string;
  outcome?: GoalOutcome;
  outcome_date?: string;
  outcome_notes?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: number;
}

export interface ServiceGoalCreate {
  goal_description: string;
  goal_category?: string;
  target_date?: string;
}

export interface ServiceGoalUpdate {
  goal_description?: string;
  goal_category?: string;
  target_date?: string;
  status?: GoalStatus;
  progress_percentage?: number;
  progress_notes?: string;
  outcome?: GoalOutcome;
  outcome_notes?: string;
}

// ============================================================================
// TYPE DEFINITIONS - MILESTONES
// ============================================================================

export type MilestoneType = 'completion' | 'time_based' | 'outcome' | 'engagement';
export type MilestoneStatus = 'pending' | 'in_progress' | 'achieved' | 'skipped' | 'failed';

export interface MilestoneDefinition {
  id: number;
  service_type_code: string;
  program_type: string;
  milestone_name: string;
  milestone_code: string;
  description?: string;
  milestone_type: MilestoneType;
  sequence_number: number;
  is_required: boolean;
  target_days_from_start?: number;
  incentive_amount?: number;
  incentive_type?: string;
  verification_required: boolean;
  verification_type?: string;
  is_active: boolean;
}

export interface MilestoneAchievement {
  id: number;
  referral_id: number;
  milestone_definition_id: number;
  milestone_name: string;
  milestone_code: string;
  milestone_type: MilestoneType;
  sequence_number: number;
  is_required: boolean;
  target_days_from_start?: number;
  target_date?: string;
  status: MilestoneStatus;
  progress_percentage: number;
  progress_notes?: string;
  achieved_at?: string;
  achievement_notes?: string;
  incentive_amount?: number;
  incentive_triggered: boolean;
  incentive_amount_earned?: number;
  incentive_triggered_at?: string;
  days_since_start?: number;
  is_overdue: boolean;
  created_at: string;
  updated_at: string;
}

export interface MilestoneAchieveRequest {
  achievement_notes?: string;
}

export interface MilestoneStatusSummary {
  total_milestones: number;
  required_milestones: number;
  achieved_milestones: number;
  required_achieved: number;
  all_required_complete: boolean;
  total_incentive_possible: number;
  total_incentive_earned: number;
}

// ============================================================================
// TYPE DEFINITIONS - BILLING
// ============================================================================

export type BillingStatus = 'pending' | 'submitted' | 'paid' | 'partial_paid' | 'denied' | 'appealed';
export type CodeSystem = 'CPT' | 'HCPCS' | 'custom';

export interface BillingRecord {
  id: number;
  referral_id: number;
  provider_id: number;
  employee_id: number;
  employer_id: number;
  billing_code: string;
  code_system: CodeSystem;
  modifier_codes?: string[];
  diagnosis_codes?: string[];
  place_of_service?: string;
  units: number;
  rate_per_unit: number;
  total_amount: number;
  amount_paid: number;
  adjustment_amount: number;
  billing_status: BillingStatus;
  service_date: string;
  submitted_at?: string;
  submission_reference?: string;
  paid_at?: string;
  prior_auth_number?: string;
  prior_auth_required: boolean;
  denial_reason?: string;
  denial_code?: string;
  appeal_deadline?: string;
  appeal_submitted_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: number;
}

export interface BillingRecordCreate {
  billing_code: string;
  code_system?: CodeSystem;
  modifier_codes?: string[];
  diagnosis_codes?: string[];
  place_of_service?: string;
  units?: number;
  rate_per_unit: number;
  service_date: string;
  prior_auth_number?: string;
  prior_auth_required?: boolean;
  notes?: string;
}

export interface BillingSummary {
  total_billed: number;
  total_paid: number;
  total_adjusted: number;
  balance_due: number;
  billing_count: number;
}

export interface PaymentModel {
  id: string;
  name: string;
  description: string;
  available: boolean;
}

export interface AcceptedPricing {
  proposal_id: number;
  rate: number | null;
  units: number;
  unit_type: string | null;
  total_amount: number | null;
  status: string;
  accepted_at: string | null;
  billing_code: string | null;
  payment_terms: string | null;
  payment_schedule: string | null;
}

export interface ConsentStatus {
  status: string;
  consent_date: string | null;
  expiration_date: string | null;
  verified_at: string | null;
  is_valid: boolean;
}

export interface BillingContext {
  referral_id: number;
  status: string;
  service_name: string;
  service_type_code: string;
  employee: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
  employer: {
    id: number;
    name: string | null;
  };
  service_delivery: {
    completed_at: string | null;
    outcome: string | null;
    actual_units: number | null;
  };
  pricing: {
    estimated_rate: number | null;
    estimated_units: number | null;
    billing_unit: string | null;
    billable_amount: number | null;
  };
  accepted_pricing: AcceptedPricing | null;
  consent_status: ConsentStatus | null;
  milestones: Array<{
    definition_id: number;
    name: string;
    type: string;
    status: string;
    achieved_at: string | null;
    incentive_amount: number;
  }>;
  billing_ready: boolean;
  requires_consent_confirmation: boolean;
  payment_models: PaymentModel[];
}

// ============================================================================
// TYPE DEFINITIONS - CBO REIMBURSEMENT
// ============================================================================

export interface McpBillingRule {
  activity_code: string | null;
  hcpcs_code: string | null;
  cpt_code: string | null;
  activity_name: string | null;
  rate_per_unit: number;
  billing_unit: string | null;
}

export interface McpReimbursementContract {
  contract_number: string;
  mcp_name: string;
  program_id: number;
  program_name: string;
  terms: {
    enrollment_target: number;
    total_budget: number;
    avg_pmpm_rate: number;
    baseline_pmpm_cost: number;
    target_pmpm_cost: number;
    roi_horizon_months: number;
  };
  actuals: {
    enrollments: number;
    medicaid_revenue: number;
    cost_savings: number;
    roi_percentage: number;
  };
  claims: {
    count: number;
    billed: number;
    paid: number;
  };
  active_enrollments: number;
  enrollment_attribution: {
    enrollment_id: number | null;
    member_id: number;
    member_name: string | null;
    claim_count: number;
    billed: number;
    paid: number;
  }[];
  billing_rules: McpBillingRule[];
}

export interface McpContractsReconciliation {
  contract_claims_billed: number;
  contract_claims_paid: number;
  org_claims_billed: number;
  org_claims_paid: number;
  org_claim_count: number;
  reconciled: boolean;
}

export interface McpContractsResponse {
  contracts: McpReimbursementContract[];
  count: number;
  reconciliation?: McpContractsReconciliation;
}

export interface ReimbursementContract {
  id: number;
  provider_id: number;
  employer_id: number;
  contract_name: string;
  contract_type: string;
  effective_date: string;
  end_date?: string;
  status: string;
  auto_renew: boolean;
  total_contract_amount?: number;
  amount_utilized: number;
  amount_remaining?: number;
  created_at: string;
  updated_at: string;
}

export interface ReimbursementRate {
  id: number;
  contract_id: number;
  service_type_code: string;
  service_type_name?: string;
  billing_code: string;
  rate_per_unit: number;
  unit_type: string;
  max_units_per_service?: number;
  max_amount_per_service?: number;
  effective_date: string;
  end_date?: string;
  is_active: boolean;
}

export interface ReimbursementInvoice {
  id: number;
  provider_id: number;
  employer_id: number;
  contract_id?: number;
  invoice_number: string;
  billing_period_start: string;
  billing_period_end: string;
  total_amount: number;
  tax_amount: number;
  grand_total: number;
  status: string;
  due_date?: string;
  paid_at?: string;
  payment_reference?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ReimbursementReconciliation {
  id: number;
  invoice_id: number;
  billing_record_id: number;
  referral_id: number;
  employee_id: number;
  service_type_code: string;
  service_date: string;
  units: number;
  rate: number;
  amount: number;
  adjustment_amount: number;
  final_amount: number;
  reconciliation_status: string;
  variance_reason?: string;
  created_at: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class ServiceReferralsAPI {
  private client: AxiosInstance;

  constructor() {
    initializeActiveCboId();

    // Use the shared apiClient that points to WeaveIn API (weavein-api.onrender.com)
    // Service referrals are consolidated into the WeaveIn API backend
    this.client = apiClient;

    // Add X-CBO-ID header via interceptor (apiClient already handles auth token)
    this.client.interceptors.request.use((config) => {
      const cboId = localStorage.getItem('active_cbo_id');
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });
  }

  // ========================================================================
  // SERVICE TAXONOMY ENDPOINTS
  // ========================================================================

  async getServiceDomains(activeOnly: boolean = true): Promise<ServiceDomain[]> {
    const response = await this.client.get<ServiceDomain[]>('/api/service-referrals/taxonomy/domains', {
      params: { active_only: activeOnly },
    });
    return response.data;
  }

  async getServiceCategories(domainId?: number, activeOnly: boolean = true): Promise<ServiceCategory[]> {
    const response = await this.client.get<ServiceCategory[]>('/api/service-referrals/taxonomy/categories', {
      params: { domain_id: domainId, active_only: activeOnly },
    });
    return response.data;
  }

  async getServiceTypes(categoryId?: number, activeOnly: boolean = true): Promise<ServiceType[]> {
    const response = await this.client.get<ServiceType[]>('/api/service-referrals/taxonomy/types', {
      params: { category_id: categoryId, active_only: activeOnly },
    });
    return response.data;
  }

  async searchServiceTypes(query: string, limit: number = 20): Promise<ServiceTypeSearchResult[]> {
    const response = await this.client.get<ServiceTypeSearchResult[]>('/api/service-referrals/taxonomy/search', {
      params: { query, limit },
    });
    return response.data;
  }

  // ========================================================================
  // SERVICE REFERRAL ENDPOINTS
  // ========================================================================

  async createReferral(data: ServiceReferralCreate): Promise<ServiceReferral> {
    const response = await this.client.post<ServiceReferral>('/api/service-referrals', data);
    return response.data;
  }

  async listReferrals(params?: {
    employee_id?: number;
    employer_id?: number;
    status?: ReferralStatus;
    service_type_code?: string;
    urgency?: UrgencyLevel;
    page?: number;
    page_size?: number;
  }): Promise<ServiceReferralListResponse> {
    // Backend returns an array directly, not wrapped in {referrals: []}
    const response = await this.client.get<ServiceReferral[]>('/api/service-referrals', { params });
    const referrals = Array.isArray(response.data) ? response.data : [];
    return {
      referrals,
      total: referrals.length,
      page: params?.page || 1,
      page_size: params?.page_size || referrals.length,
    };
  }

  // Convenience method that returns just the referrals array
  async getReferrals(params?: {
    employee_id?: number;
    employer_id?: number;
    status?: ReferralStatus;
    service_type_code?: string;
    urgency?: UrgencyLevel;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  }): Promise<ServiceReferral[]> {
    // Backend returns array directly, so fetch directly instead of through listReferrals
    const response = await this.client.get<ServiceReferral[]>('/api/service-referrals', { params });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getReferral(referralId: number): Promise<ServiceReferral> {
    const response = await this.client.get<ServiceReferral>(`/api/service-referrals/${referralId}`);
    return response.data;
  }

  async getMatches(referralId: number): Promise<ReferralMatch[]> {
    const response = await this.client.get<ReferralMatch[]>(`/api/service-referrals/${referralId}/matches`);
    return response.data;
  }

  async updateReferral(referralId: number, data: ServiceReferralUpdate): Promise<ServiceReferral> {
    const response = await this.client.patch<ServiceReferral>(`/api/service-referrals/${referralId}`, data);
    return response.data;
  }

  async transitionReferralStatus(
    referralId: number,
    newStatus: ReferralStatus,
    notes?: string
  ): Promise<StatusTransitionResponse> {
    const response = await this.client.post<StatusTransitionResponse>(`/api/service-referrals/${referralId}/transition`, {
      new_status: newStatus,
      notes,
    });
    return response.data;
  }

  // ========================================================================
  // SERVICE DELIVERY PROGRESS ENDPOINTS
  // ========================================================================

  async getProgress(referralId: number): Promise<ServiceProgress[]> {
    const response = await this.client.get<ServiceProgress[]>(`/api/service-referrals/${referralId}/progress`);
    return response.data;
  }

  async addProgress(referralId: number, data: ServiceProgressCreate): Promise<ServiceProgress> {
    const response = await this.client.post<ServiceProgress>(`/api/service-referrals/${referralId}/progress`, data);
    return response.data;
  }

  // ========================================================================
  // SERVICE GOALS ENDPOINTS
  // ========================================================================

  async getGoals(referralId: number): Promise<ServiceGoal[]> {
    const response = await this.client.get<ServiceGoal[]>(`/api/service-referrals/${referralId}/goals`);
    return response.data;
  }

  async addGoal(referralId: number, data: ServiceGoalCreate): Promise<ServiceGoal> {
    const response = await this.client.post<ServiceGoal>(`/api/service-referrals/${referralId}/goals`, data);
    return response.data;
  }

  async updateGoal(referralId: number, goalId: number, data: ServiceGoalUpdate): Promise<ServiceGoal> {
    const response = await this.client.patch<ServiceGoal>(
      `/api/service-referrals/${referralId}/goals/${goalId}`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // MILESTONE ENDPOINTS
  // ========================================================================

  async getMilestones(referralId: number): Promise<MilestoneAchievement[]> {
    const response = await this.client.get<MilestoneAchievement[]>(`/api/service-referrals/${referralId}/milestones`);
    return response.data;
  }

  async achieveMilestone(
    referralId: number,
    milestoneDefId: number,
    data?: MilestoneAchieveRequest
  ): Promise<MilestoneAchievement> {
    const response = await this.client.post<MilestoneAchievement>(
      `/api/service-referrals/${referralId}/milestones/${milestoneDefId}/achieve`,
      data || {}
    );
    return response.data;
  }

  async getMilestoneStatus(referralId: number): Promise<MilestoneStatusSummary> {
    const milestones = await this.getMilestones(referralId);
    const achieved = milestones.filter((m) => m.status === 'achieved');
    const required = milestones.filter((m) => m.is_required);
    const requiredAchieved = milestones.filter((m) => m.is_required && m.status === 'achieved');

    return {
      total_milestones: milestones.length,
      required_milestones: required.length,
      achieved_milestones: achieved.length,
      required_achieved: requiredAchieved.length,
      all_required_complete: required.length === requiredAchieved.length,
      total_incentive_possible: milestones.reduce((sum, m) => sum + (m.incentive_amount || 0), 0),
      total_incentive_earned: milestones.reduce((sum, m) => sum + (m.incentive_amount_earned || 0), 0),
    };
  }

  // ========================================================================
  // BILLING ENDPOINTS
  // ========================================================================

  async getBillingRecords(referralId: number): Promise<BillingRecord[]> {
    const response = await this.client.get<BillingRecord[]>(`/api/service-referrals/${referralId}/billing`);
    return response.data;
  }

  async createBillingRecord(referralId: number, data: BillingRecordCreate): Promise<BillingRecord> {
    const response = await this.client.post<BillingRecord>(`/api/service-referrals/${referralId}/billing`, data);
    return response.data;
  }

  async getBillingSummary(referralId: number): Promise<BillingSummary> {
    const response = await this.client.get<BillingSummary>(`/api/service-referrals/${referralId}/billing/summary`);
    return response.data;
  }

  async getBillingContext(referralId: number): Promise<BillingContext> {
    const response = await this.client.get<BillingContext>(`/api/service-referrals/${referralId}/billing/context`);
    return response.data;
  }

  async confirmServiceDelivery(referralId: number, notes?: string): Promise<{ success: boolean; message: string; new_status: string }> {
    const response = await this.client.post(`/api/service-referrals/${referralId}/billing/confirm-delivery`, null, {
      params: { confirmation_notes: notes },
    });
    return response.data;
  }

  async processBilling(
    referralId: number,
    paymentModel: 'invoice' | 'auto_ach' | 'prepaid_card' = 'invoice'
  ): Promise<{
    success: boolean;
    billing_id: number;
    amount: number;
    payment_model: string;
    message: string;
    next_step: string;
    new_status?: string;
    payment_id?: number;
    payment_status?: 'pending' | 'processing' | 'completed' | 'pending_bank_setup';
    card_token?: string;
    card_last4?: string;
    error_detail?: string;
  }> {
    const response = await this.client.post(`/api/service-referrals/${referralId}/billing/process`, null, {
      params: { payment_model: paymentModel },
    });
    return response.data;
  }

  // ========================================================================
  // CBO REIMBURSEMENT ENDPOINTS
  // ========================================================================

  async getReimbursementContracts(params?: {
    employer_id?: number;
    status?: string;
  }): Promise<ReimbursementContract[]> {
    const response = await this.client.get<ReimbursementContract[]>('/api/cbo-reimbursement/contracts', { params });
    return response.data;
  }

  async getMcpReimbursementContracts(): Promise<McpContractsResponse> {
    const response = await this.client.get<McpContractsResponse>('/api/cbo-reimbursement/mcp-contracts');
    return response.data;
  }

  async getReimbursementContract(contractId: number): Promise<ReimbursementContract> {
    const response = await this.client.get<ReimbursementContract>(`/api/cbo-reimbursement/contracts/${contractId}`);
    return response.data;
  }

  async getContractRates(contractId: number): Promise<ReimbursementRate[]> {
    const response = await this.client.get<ReimbursementRate[]>(
      `/api/cbo-reimbursement/contracts/${contractId}/rates`
    );
    return response.data;
  }

  async getReimbursementInvoices(params?: {
    employer_id?: number;
    contract_id?: number;
    status?: string;
  }): Promise<ReimbursementInvoice[]> {
    const response = await this.client.get<ReimbursementInvoice[]>('/api/cbo-reimbursement/invoices', { params });
    return response.data;
  }

  async getInvoice(invoiceId: number): Promise<ReimbursementInvoice> {
    const response = await this.client.get<ReimbursementInvoice>(`/api/cbo-reimbursement/invoices/${invoiceId}`);
    return response.data;
  }

  async getInvoiceReconciliation(invoiceId: number): Promise<ReimbursementReconciliation[]> {
    const response = await this.client.get<ReimbursementReconciliation[]>(
      `/api/cbo-reimbursement/invoices/${invoiceId}/reconciliation`
    );
    return response.data;
  }

  // ========================================================================
  // PROVIDER-SPECIFIC ENDPOINTS
  // ========================================================================

  async getProviderReferrals(params?: {
    status?: ReferralStatus;
    service_type_code?: string;
    page?: number;
    page_size?: number;
  }): Promise<ServiceReferralListResponse> {
    const response = await this.client.get<ServiceReferralListResponse>('/api/service-referrals/provider/referrals', {
      params,
    });
    return response.data;
  }

  async acceptReferral(referralId: number, notes?: string): Promise<StatusTransitionResponse> {
    return this.transitionReferralStatus(referralId, 'provider_accepted', notes);
  }

  async declineReferral(referralId: number, reason: string): Promise<StatusTransitionResponse> {
    return this.transitionReferralStatus(referralId, 'provider_declined', reason);
  }

  async startService(referralId: number, startDate?: string): Promise<ServiceReferral> {
    const response = await this.client.patch<ServiceReferral>(`/api/service-referrals/${referralId}`, {
      status: 'service_started',
      actual_start_date: startDate || new Date().toISOString().split('T')[0],
    });
    return response.data;
  }

  async completeService(
    referralId: number,
    outcomeStatus: string,
    outcomeNotes?: string
  ): Promise<ServiceReferral> {
    const response = await this.client.patch<ServiceReferral>(`/api/service-referrals/${referralId}`, {
      status: 'service_completed',
      actual_end_date: new Date().toISOString().split('T')[0],
      outcome_status: outcomeStatus,
      outcome_notes: outcomeNotes,
    });
    return response.data;
  }

  // ========================================================================
  // CONSENT-REFERRAL INTEGRATION ENDPOINTS
  // ========================================================================

  /**
   * Get consent context for a referral (for CBO workspace display).
   * Returns null if not a consent-based referral.
   */
  async getConsentContext(referralId: number): Promise<ConsentContext | null> {
    const response = await this.client.get<{ consent_context: ConsentContext | null }>(
      `/api/service-referrals/${referralId}/consent-context`
    );
    return response.data.consent_context;
  }

  /**
   * Get referral(s) created from a specific consent.
   */
  async getReferralsByConsent(consentId: number): Promise<ConsentReferralLink[]> {
    const response = await this.client.get<{ referrals: ConsentReferralLink[]; total: number }>(
      `/api/service-referrals/from-consent/${consentId}`
    );
    return response.data.referrals;
  }

  /**
   * Get pending referrals created from consents (for CBO dashboard).
   */
  async getPendingConsentReferrals(params?: {
    cbo_id?: number;
    employer_id?: number;
    limit?: number;
  }): Promise<ConsentSourceReferral[]> {
    const response = await this.client.get<{ referrals: ConsentSourceReferral[]; total: number }>(
      '/api/service-referrals/consent-source/pending',
      { params }
    );
    return response.data.referrals;
  }

  /**
   * Get pending consents that should have referrals created (CBO dashboard).
   */
  async getPendingConsentsForReferral(params?: {
    cbo_id?: number;
    employer_id?: number;
    limit?: number;
  }): Promise<PendingConsentForReferral[]> {
    const response = await this.client.get<{ consents: PendingConsentForReferral[]; total: number }>(
      '/api/employee-consent/pending-for-referral',
      { params }
    );
    return response.data.consents;
  }

  /**
   * Manually create a referral from a consent (fallback flow).
   */
  async createReferralFromConsent(params: {
    consent_id: number;
    service_type_code: string;
    urgency?: string;
    notes?: string;
  }): Promise<ServiceReferral> {
    const response = await this.client.post<{ referral: ServiceReferral }>(
      '/api/employee-consent/create-referral',
      params
    );
    return response.data.referral;
  }

  // ========================================================================
  // STAFF/ADMIN ACTION ENDPOINTS
  // ========================================================================

  /**
   * Accept consent on behalf of an employee (staff action).
   * Use when employee has verbally consented or needs staff assistance.
   */
  async acceptConsentOnBehalf(params: {
    employee_id: number;
    program_id: number;
    consent_text?: string;
    notes?: string;
    create_referral?: boolean;
  }): Promise<StaffConsentAcceptResponse> {
    const response = await this.client.post<StaffConsentAcceptResponse>(
      '/api/employee-consent/admin/accept-on-behalf',
      params
    );
    return response.data;
  }

  /**
   * Create a manual service referral for an employee (staff action).
   * Bypasses consent flow for urgent situations.
   * Supports SDOH assessment integration.
   */
  async createManualReferral(params: {
    employee_id: number;
    service_type_code: string;
    urgency?: 'routine' | 'urgent' | 'emergency';
    service_description?: string;
    trigger_matching?: boolean;
    // SDOH Assessment fields
    sdoh_domain_code?: string;
    sdoh_risk_level?: string;
    sdoh_needs?: Array<{
      sdoh_domain_code: string;
      severity: string;
      is_primary: boolean;
      need_description?: string;
    }>;
    clinical_notes?: string;
  }): Promise<StaffManualReferralResponse> {
    const response = await this.client.post<StaffManualReferralResponse>(
      '/api/employee-consent/admin/create-manual-referral',
      params
    );
    return response.data;
  }

  // ========================================================================
  // NETWORK REFERRALS (TRUS-749) — send a member to another org's Referral Inbox
  // ========================================================================

  /** Orgs the active org can refer to (never itself). Network partners first. */
  async listReferableOrgs(): Promise<ReferableOrg[]> {
    const response = await this.client.get<ReferableOrg[]>('/api/network-referrals/orgs');
    return response.data;
  }

  /** Creates the referral and puts it in the receiving org's Referral Inbox. */
  async referToOrg(data: ReferToOrgRequest): Promise<ReferToOrgResponse> {
    const response = await this.client.post<ReferToOrgResponse>('/api/network-referrals', data);
    return response.data;
  }

  /**
   * Get comprehensive consent and referral status for an employee.
   * Used by workflow orchestration to show employee detail panel.
   */
  async getEmployeeConsentReferralStatus(employeeId: number): Promise<EmployeeConsentReferralStatus> {
    const response = await this.client.get<EmployeeConsentReferralStatus>(
      `/api/employee-consent/admin/employee/${employeeId}/status`
    );
    return response.data;
  }

  /**
   * Initiate a workflow for an employee (staff action).
   * Starts the consent/referral workflow process for an employee.
   */
  async initiateWorkflow(params: {
    employee_id: number;
    template_id: number;
    notes?: string;
  }): Promise<WorkflowInitiateResponse> {
    const response = await this.client.post<WorkflowInitiateResponse>(
      '/api/employee-consent/admin/initiate-workflow',
      params
    );
    return response.data;
  }

  // ========================================================================
  // SDOH ASSESSMENT ENDPOINTS
  // ========================================================================

  /**
   * Save or update SDOH assessment for a referral
   */
  async saveSDOHAssessment(
    referralId: number,
    data: SDOHAssessmentSaveRequest
  ): Promise<SDOHAssessmentSaveResponse> {
    const response = await this.client.post<SDOHAssessmentSaveResponse>(
      `/api/service-referrals/${referralId}/sdoh-assessment`,
      data
    );
    return response.data;
  }

  /**
   * Get SDOH assessment for a referral
   */
  async getSDOHAssessment(referralId: number): Promise<SDOHAssessmentForReferral> {
    const response = await this.client.get<SDOHAssessmentForReferral>(
      `/api/service-referrals/${referralId}/sdoh-assessment`
    );
    return response.data;
  }

  // ========================================================================
  // EXTERNAL SDOH DATA ENDPOINTS
  // ========================================================================

  /**
   * Get comprehensive SDOH data for a ZIP code
   * Includes transportation, housing, climate, and area-level SDOH data
   */
  async getComprehensiveSDOHByZip(zipCode: string, autoSeed: boolean = true): Promise<ComprehensiveSDOHData> {
    const response = await this.client.get<ComprehensiveSDOHData>(
      `/api/sdoh-data/zip/${zipCode}/comprehensive`,
      { params: { auto_seed: autoSeed } }
    );
    return response.data;
  }

  /**
   * Get transportation scores for a ZIP code
   */
  async getTransportationScores(zipCode: string): Promise<TransportationScoreData | null> {
    const response = await this.client.get<TransportationScoreData | null>(
      `/api/sdoh-data/zip/${zipCode}/transportation`
    );
    return response.data;
  }

  /**
   * Get housing risk data for a ZIP code
   */
  async getHousingRiskData(zipCode: string): Promise<HousingRiskData | null> {
    const response = await this.client.get<HousingRiskData | null>(
      `/api/sdoh-data/zip/${zipCode}/housing`
    );
    return response.data;
  }

  /**
   * Get climate risk data for a ZIP code
   */
  async getClimateRiskData(zipCode: string): Promise<ClimateRiskData | null> {
    const response = await this.client.get<ClimateRiskData | null>(
      `/api/sdoh-data/zip/${zipCode}/climate`
    );
    return response.data;
  }

  /**
   * Get enhanced referral data with SDOH context from workflow and ML analysis
   */
  async getReferralSDOHContext(referralId: number): Promise<ReferralSDOHContext> {
    const response = await this.client.get<ReferralSDOHContext>(
      `/api/service-referrals/${referralId}/sdoh-context`
    );
    return response.data;
  }
}

// Export singleton instance - uses WeaveIn API (weavein-api.onrender.com)
// Network referrals (TRUS-749). The backend's urgency enum is 'emergent', not
// the 'emergency' that UrgencyLevel above carries.
export type NetworkReferralUrgency = 'routine' | 'urgent' | 'emergent';

export interface ReferableOrg {
  id: number;
  name: string;
  /** Shares a program with the active org. */
  shares_program: boolean;
}

export interface ReferToOrgRequest {
  member_id: number;
  receiving_cbo_id: number;
  service_type_code: string;
  urgency_level: NetworkReferralUrgency;
  notes?: string;
}

export interface ReferToOrgResponse {
  referral_id: number;
  referral_number: string | null;
  match_id: number;
  status: string;
  receiving_org: { id: number; name: string };
  service_type_code: string;
  service_type_name: string;
  urgency_level: NetworkReferralUrgency;
  acceptance_expires_at: string;
}

export const serviceReferralsAPI = new ServiceReferralsAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getReferralStatusLabel(status: ReferralStatus): string {
  const labels: Record<ReferralStatus, string> = {
    created: 'Created',
    pending_consent: 'Pending Consent',
    consent_obtained: 'Consent Obtained',
    matching: 'Matching Provider',
    matched: 'Provider Matched',
    provider_notified: 'Provider Notified',
    provider_accepted: 'Provider Accepted',
    provider_declined: 'Provider Declined',
    service_scheduled: 'Service Scheduled',
    service_started: 'Service In Progress',
    service_completed: 'Service Completed',
    billing_ready: 'Ready for Billing',
    billed: 'Billed',
    paid: 'Paid',
    closed: 'Closed',
    cancelled: 'Cancelled',
    failed: 'Failed',
  };
  return labels[status] || status;
}

export function getReferralStatusColor(status: ReferralStatus): string {
  const colors: Record<ReferralStatus, string> = {
    created: 'gray',
    pending_consent: 'yellow',
    consent_obtained: 'blue',
    matching: 'blue',
    matched: 'blue',
    provider_notified: 'blue',
    provider_accepted: 'green',
    provider_declined: 'red',
    service_scheduled: 'green',
    service_started: 'green',
    service_completed: 'green',
    billing_ready: 'purple',
    billed: 'purple',
    paid: 'green',
    closed: 'gray',
    cancelled: 'red',
    failed: 'red',
  };
  return colors[status] || 'gray';
}

export function getUrgencyLabel(urgency: UrgencyLevel): string {
  const labels: Record<UrgencyLevel, string> = {
    routine: 'Routine',
    urgent: 'Urgent',
    emergency: 'Emergency',
  };
  return labels[urgency] || urgency;
}

export function getUrgencyColor(urgency: UrgencyLevel): string {
  const colors: Record<UrgencyLevel, string> = {
    routine: 'gray',
    urgent: 'orange',
    emergency: 'red',
  };
  return colors[urgency] || 'gray';
}

export function getMilestoneStatusLabel(status: MilestoneStatus): string {
  const labels: Record<MilestoneStatus, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    achieved: 'Achieved',
    skipped: 'Skipped',
    failed: 'Failed',
  };
  return labels[status] || status;
}

export function getBillingStatusLabel(status: BillingStatus): string {
  const labels: Record<BillingStatus, string> = {
    pending: 'Pending',
    submitted: 'Submitted',
    paid: 'Paid',
    partial_paid: 'Partially Paid',
    denied: 'Denied',
    appealed: 'Under Appeal',
  };
  return labels[status] || status;
}

// ============================================================================
// SDOH ASSESSMENT TYPES
// ============================================================================

export interface SDOHAssessmentSaveRequest {
  assessment_type?: string;
  assessment_tool?: string;
  housing_score?: number;
  food_score?: number;
  transportation_score?: number;
  economic_score?: number;
  education_employment_score?: number;
  social_support_score?: number;
  health_behaviors_score?: number;
  safety_score?: number;
  needs?: Array<{
    domain_code: SDOHDomainCode;
    category_code?: string;
    description?: string;
    severity?: SDOHSeverity;
    is_primary?: boolean;
  }>;
  requires_follow_up?: boolean;
  follow_up_date?: string;
  notes?: string;
}

export interface SDOHAssessmentSaveResponse {
  success: boolean;
  message: string;
  assessment_id: number;
  referral_id: number;
  overall_score?: number;
  risk_level?: string;
  priority_domains?: string[];
  needs_count: number;
}

export interface SDOHAssessmentForReferral {
  referral_id: number;
  sdoh_domain_code?: string;
  sdoh_risk_score?: number;
  assessment?: SDOHAssessmentData;
  needs: SDOHNeedData[];
}

// ============================================================================
// EXTERNAL SDOH DATA TYPES
// ============================================================================

export interface TransportationScoreData {
  zip_code: string;
  walk_score?: number;
  walk_score_description?: string;
  transit_score?: number;
  transit_score_description?: string;
  bike_score?: number;
  bike_score_description?: string;
  pct_drive_alone?: number;
  pct_public_transit?: number;
  pct_work_from_home?: number;
  pct_no_vehicle_households?: number;
  mean_commute_time_minutes?: number;
  transportation_access_score?: number;
  transportation_barrier_score?: number;
  last_updated?: string;
}

export interface HousingRiskData {
  zip_code: string;
  eviction_rate?: number;
  eviction_filing_rate?: number;
  eviction_trend?: string;
  pct_cost_burdened?: number;
  pct_severely_cost_burdened?: number;
  median_gross_rent?: number;
  median_home_value?: number;
  vacancy_rate?: number;
  pct_overcrowded?: number;
  pct_lacking_complete_plumbing?: number;
  homeless_per_10k_population?: number;
  unsheltered_rate?: number;
  housing_stability_score?: number;
  housing_affordability_score?: number;
  housing_quality_score?: number;
  housing_risk_score?: number;
  last_updated?: string;
}

export interface ClimateRiskData {
  zip_code: string;
  aqi_annual_median?: number;
  aqi_days_good?: number;
  aqi_days_unhealthy?: number;
  primary_pollutant?: string;
  flood_zone?: string;
  flood_risk_level?: string;
  historical_flood_events_10y?: number;
  wildfire_risk_level?: string;
  wildfire_risk_score?: number;
  climate_risk_score?: number;
  environmental_health_score?: number;
  natural_disaster_risk_score?: number;
  last_updated?: string;
}

export interface AreaSDOHData {
  zip_code: string;
  median_household_income?: number;
  poverty_rate?: number;
  unemployment_rate?: number;
  snap_participation_rate?: number;
  medicaid_enrollment_rate?: number;
  high_school_graduation_rate?: number;
  college_degree_rate?: number;
  uninsured_rate?: number;
  pcp_per_100k?: number;
  limited_english_rate?: number;
  disability_rate?: number;
  no_vehicle_rate?: number;
  diabetes_prevalence?: number;
  obesity_prevalence?: number;
  food_desert_flag?: boolean;
  sdoh_composite_score?: number;
  last_updated?: string;
}

export interface ComprehensiveSDOHData {
  zip_code: string;
  transportation?: TransportationScoreData;
  housing?: HousingRiskData;
  climate?: ClimateRiskData;
  area_sdoh?: AreaSDOHData;
  combined_risk_score?: number;
  data_sources: string[];
  last_updated?: string;
}

export interface MLPredictedSDOH {
  transportation_barrier_probability?: number;
  transportation_barrier_confidence?: string;
  financial_barrier_probability?: number;
  financial_barrier_confidence?: string;
  housing_instability_probability?: number;
  housing_instability_confidence?: string;
  predicted_at?: string;
  evidence_summary?: string;
}

export interface WorkflowSDOHContext {
  workflow_id?: number;
  workflow_status?: string;
  initial_sdoh_score?: number;
  initial_risk_level?: string;
  trigger_reason?: string;
  referral_source_category?: string;
  source_description?: string;
}

export interface ReferralSDOHContext {
  referral_id: number;
  service_type_code: string;
  service_type_name?: string;
  member_zip_code?: string;
  // Assessment data
  assessment?: SDOHAssessmentData;
  needs?: SDOHNeedData[];
  // ML predictions
  ml_predictions?: MLPredictedSDOH;
  // Area-level data
  area_data?: ComprehensiveSDOHData;
  // Workflow context
  workflow_context?: WorkflowSDOHContext;
  // Summary
  overall_risk_level?: string;
  priority_domains?: string[];
  recommended_services?: string[];
}

export default serviceReferralsAPI;
